import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import AsyncSelect from 'react-select/async';
import { 
  Clock,
  MapPin,
  Calendar,
  User,
  Sparkles,
  ArrowRight,
  RefreshCw,
  Info,
  ArrowLeft,
  Search,
  Save
} from 'lucide-react';
import "bulma/css/bulma.min.css"; // Import Bulma CSS
import '../assets/ascendant-display.sass'; // Import ascendant display styles
import { generateBirthChart } from './ephemerisUtils';
import { calculateBirthChartWithLibrary } from './horoscopeCalculator';
import useCityToCoordinates from '../components/tools/useCityToCoordinates';
import ChartDrawer from '../components/tools/ChartDrawer';
import cityTimezones from 'city-timezones';
import { useFirebase } from '../context/firebaseContext';
import { useAuth } from '../context/authContext';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';

// Major cities with their coordinates - ensure no duplicates
const majorCities = [
  { name: "New York, NY, USA", lat: 40.7128, lon: -74.0060 },
  { name: "Los Angeles, CA, USA", lat: 34.0522, lon: -118.2437 },
  { name: "Chicago, IL, USA", lat: 41.8781, lon: -87.6298 },
  { name: "Houston, TX, USA", lat: 29.7604, lon: -95.3698 },
  { name: "Phoenix, AZ, USA", lat: 33.4484, lon: -112.0740 },
  { name: "Philadelphia, PA, USA", lat: 39.9526, lon: -75.1652 },
  { name: "San Antonio, TX, USA", lat: 29.4241, lon: -98.4936 },
  { name: "San Diego, CA, USA", lat: 32.7157, lon: -117.1611 },
  { name: "Dallas, TX, USA", lat: 32.7767, lon: -96.7970 },
  { name: "San Jose, CA, USA", lat: 37.3382, lon: -121.8863 },
  { name: "London, UK", lat: 51.5074, lon: -0.1278 },
  { name: "Paris, France", lat: 48.8566, lon: 2.3522 },
  { name: "Berlin, Germany", lat: 52.5200, lon: 13.4050 },
  { name: "Madrid, Spain", lat: 40.4168, lon: -3.7038 },
  { name: "Rome, Italy", lat: 41.9028, lon: 12.4964 },
  { name: "Amsterdam, Netherlands", lat: 52.3676, lon: 4.9041 },
  { name: "Vienna, Austria", lat: 48.2082, lon: 16.3738 },
  { name: "Prague, Czech Republic", lat: 50.0755, lon: 14.4378 },
  { name: "Budapest, Hungary", lat: 47.4979, lon: 19.0402 },
  { name: "Warsaw, Poland", lat: 52.2297, lon: 21.0122 },
  { name: "Moscow, Russia", lat: 55.7558, lon: 37.6176 },
  { name: "Tokyo, Japan", lat: 35.6762, lon: 139.6503 },
  { name: "Seoul, South Korea", lat: 37.5665, lon: 126.9780 },
  { name: "Beijing, China", lat: 39.9042, lon: 116.4074 },
  { name: "Shanghai, China", lat: 31.2304, lon: 121.4737 },
  { name: "Hong Kong", lat: 22.3193, lon: 114.1694 },
  { name: "Singapore", lat: 1.3521, lon: 103.8198 },
  { name: "Bangkok, Thailand", lat: 13.7563, lon: 100.5018 },
  { name: "Jakarta, Indonesia", lat: -6.2088, lon: 106.8456 },
  { name: "Kuala Lumpur, Malaysia", lat: 3.1390, lon: 101.6869 },
  { name: "Manila, Philippines", lat: 14.5995, lon: 120.9842 },
  { name: "Ho Chi Minh City, Vietnam", lat: 10.8231, lon: 106.6297 },
  { name: "Mumbai, India", lat: 19.0760, lon: 72.8777 },
  { name: "Delhi, India", lat: 28.7041, lon: 77.1025 },
  { name: "Bangalore, India", lat: 12.9716, lon: 77.5946 },
  { name: "Chennai, India", lat: 13.0827, lon: 80.2707 },
  { name: "Kolkata, India", lat: 22.5726, lon: 88.3639 },
  { name: "Hyderabad, India", lat: 17.3850, lon: 78.4867 },
  { name: "Pune, India", lat: 18.5204, lon: 73.8567 },
  { name: "Ahmedabad, India", lat: 23.0225, lon: 72.5714 },
  { name: "Sydney, Australia", lat: -33.8688, lon: 151.2093 },
  { name: "Melbourne, Australia", lat: -37.8136, lon: 144.9631 },
  { name: "Brisbane, Australia", lat: -27.4698, lon: 153.0251 },
  { name: "Perth, Australia", lat: -31.9505, lon: 115.8605 },
  { name: "Adelaide, Australia", lat: -34.9285, lon: 138.6007 },
  { name: "Toronto, Canada", lat: 43.6532, lon: -79.3832 },
  { name: "Montreal, Canada", lat: 45.5017, lon: -73.5673 },
  { name: "Vancouver, Canada", lat: 49.2827, lon: -123.1207 },
  { name: "Calgary, Canada", lat: 51.0447, lon: -114.0719 },
  { name: "Edmonton, Canada", lat: 53.5461, lon: -113.4938 },
  { name: "Ottawa, Canada", lat: 45.4215, lon: -75.6972 },
  { name: "Mexico City, Mexico", lat: 19.4326, lon: -99.1332 },
  { name: "Guadalajara, Mexico", lat: 20.6597, lon: -103.3496 },
  { name: "Monterrey, Mexico", lat: 25.6866, lon: -100.3161 },
  { name: "Puebla, Mexico", lat: 19.0413, lon: -98.2062 },
  { name: "São Paulo, Brazil", lat: -23.5505, lon: -46.6333 },
  { name: "Rio de Janeiro, Brazil", lat: -22.9068, lon: -43.1729 },
  { name: "Brasília, Brazil", lat: -15.7942, lon: -47.8822 },
  { name: "Salvador, Brazil", lat: -12.9714, lon: -38.5011 },
  { name: "Fortaleza, Brazil", lat: -3.7319, lon: -38.5267 },
  { name: "Belo Horizonte, Brazil", lat: -19.9167, lon: -43.9345 },
  { name: "Buenos Aires, Argentina", lat: -34.6118, lon: -58.3960 },
  { name: "Córdoba, Argentina", lat: -31.4201, lon: -64.1888 },
  { name: "Rosario, Argentina", lat: -32.9468, lon: -60.6393 },
  { name: "Santiago, Chile", lat: -33.4489, lon: -70.6693 },
  { name: "Valparaíso, Chile", lat: -33.0472, lon: -71.6127 },
  { name: "Lima, Peru", lat: -12.0464, lon: -77.0428 },
  { name: "Arequipa, Peru", lat: -16.4090, lon: -71.5375 },
  { name: "Bogotá, Colombia", lat: 4.7110, lon: -74.0721 },
  { name: "Medellín, Colombia", lat: 6.2442, lon: -75.5812 },
  { name: "Cali, Colombia", lat: 3.4516, lon: -76.5320 },
  { name: "Caracas, Venezuela", lat: 10.4806, lon: -66.9036 },
  { name: "Maracaibo, Venezuela", lat: 10.6427, lon: -71.6125 },
  { name: "Quito, Ecuador", lat: -0.2299, lon: -78.5249 },
  { name: "Guayaquil, Ecuador", lat: -2.1894, lon: -79.8891 },
  { name: "Montevideo, Uruguay", lat: -34.9011, lon: -56.1645 },
  { name: "Asunción, Paraguay", lat: -25.2637, lon: -57.5759 },
  { name: "La Paz, Bolivia", lat: -16.4897, lon: -68.1193 },
  { name: "Santa Cruz, Bolivia", lat: -17.7833, lon: -63.1821 },
  { name: "Cape Town, South Africa", lat: -33.9249, lon: 18.4241 },
  { name: "Johannesburg, South Africa", lat: -26.2041, lon: 28.0473 },
  { name: "Durban, South Africa", lat: -29.8587, lon: 31.0218 },
  { name: "Pretoria, South Africa", lat: -25.7479, lon: 28.2293 },
  { name: "Port Elizabeth, South Africa", lat: -33.7139, lon: 25.5207 },
  { name: "Cairo, Egypt", lat: 30.0444, lon: 31.2357 },
  { name: "Alexandria, Egypt", lat: 31.2001, lon: 29.9187 },
  { name: "Giza, Egypt", lat: 30.0131, lon: 31.2089 },
  { name: "Lagos, Nigeria", lat: 6.5244, lon: 3.3792 },
  { name: "Kano, Nigeria", lat: 11.9914, lon: 8.5317 },
  { name: "Ibadan, Nigeria", lat: 7.3964, lon: 3.8867 },
  { name: "Kaduna, Nigeria", lat: 10.5222, lon: 7.4384 },
  { name: "Port Harcourt, Nigeria", lat: 4.8156, lon: 7.0498 },
  { name: "Nairobi, Kenya", lat: -1.2921, lon: 36.8219 },
  { name: "Mombasa, Kenya", lat: -4.0435, lon: 39.6682 },
  { name: "Kisumu, Kenya", lat: -0.1022, lon: 34.7617 },
  { name: "Addis Ababa, Ethiopia", lat: 9.0320, lon: 38.7486 },
  { name: "Dar es Salaam, Tanzania", lat: -6.8235, lon: 39.2695 },
  { name: "Mwanza, Tanzania", lat: -2.5167, lon: 32.9000 },
  { name: "Kampala, Uganda", lat: 0.3476, lon: 32.5825 },
  { name: "Khartoum, Sudan", lat: 15.5007, lon: 32.5599 },
  { name: "Casablanca, Morocco", lat: 33.5731, lon: -7.5898 },
  { name: "Rabat, Morocco", lat: 34.0209, lon: -6.8416 },
  { name: "Fez, Morocco", lat: 34.0181, lon: -5.0078 },
  { name: "Marrakech, Morocco", lat: 31.6295, lon: -7.9811 },
  { name: "Tunis, Tunisia", lat: 36.8065, lon: 10.1815 },
  { name: "Sfax, Tunisia", lat: 34.7477, lon: 10.7662 },
  { name: "Algiers, Algeria", lat: 36.7538, lon: 3.0588 },
  { name: "Oran, Algeria", lat: 35.6971, lon: -0.6359 },
  { name: "Tehran, Iran", lat: 35.6892, lon: 51.3890 },
  { name: "Mashhad, Iran", lat: 36.2972, lon: 59.6067 },
  { name: "Isfahan, Iran", lat: 32.6546, lon: 51.6680 },
  { name: "Tabriz, Iran", lat: 38.0962, lon: 46.2738 },
  { name: "Shiraz, Iran", lat: 29.5916, lon: 52.5836 },
  { name: "Karaj, Iran", lat: 35.8400, lon: 50.9391 },
  { name: "Qom, Iran", lat: 34.6416, lon: 50.8746 },
  { name: "Kermanshah, Iran", lat: 34.3277, lon: 47.0778 },
  { name: "Urmia, Iran", lat: 37.5527, lon: 45.0761 },
  { name: "Rasht, Iran", lat: 37.2809, lon: 49.5924 },
  { name: "Yazd, Iran", lat: 31.8974, lon: 54.3569 },
  { name: "Ardabil, Iran", lat: 38.2498, lon: 48.2933 },
  { name: "Bandar Abbas, Iran", lat: 27.1832, lon: 56.2666 },
  { name: "Arak, Iran", lat: 34.0954, lon: 49.7014 },
  { name: "Eslamshahr, Iran", lat: 35.5647, lon: 51.2353 },
  { name: "Zanjan, Iran", lat: 36.6736, lon: 48.4787 },
  { name: "Sanandaj, Iran", lat: 35.3219, lon: 46.9862 },
  { name: "Qazvin, Iran", lat: 36.2797, lon: 50.0049 },
  { name: "Kerman, Iran", lat: 30.2839, lon: 57.0834 },
  { name: "Hamadan, Iran", lat: 34.7983, lon: 48.5148 },
  { name: "Yasuj, Iran", lat: 30.6680, lon: 51.5880 },
  { name: "Bojnord, Iran", lat: 37.4761, lon: 57.3317 },
  { name: "Bushehr, Iran", lat: 28.9234, lon: 50.8203 },
  { name: "Gorgan, Iran", lat: 36.8427, lon: 54.4439 },
  { name: "Ilam, Iran", lat: 33.6374, lon: 46.4227 },
  { name: "Sari, Iran", lat: 36.5633, lon: 53.0601 },
  { name: "Shahrekord, Iran", lat: 32.3256, lon: 50.8644 }
];

// Bulletproof deduplication system
const uniqueCities = Array.from(
  new Map(majorCities.map(city => [city.name.toLowerCase().trim(), city])).values()
);

// Helper function to get timezone offset from coordinates using Luxon
const getTimezoneFromCoordinates = (lat, lon, birthDate = null) => {
  try {
    // If we have a birth date, use it for more accurate timezone calculation
    if (birthDate) {
      const date = DateTime.fromISO(birthDate);
      if (date.isValid) {
        // Try to get timezone from coordinates using a more sophisticated approach
        // This is a simplified version - in a real app you'd use a timezone database
        const offsetHours = Math.round(lon / 15);
        const baseOffset = offsetHours >= 0 ? `UTC+${offsetHours}` : `UTC${offsetHours}`;
        return baseOffset;
      }
    }
    
    // Fallback to simple longitude-based calculation
    const offsetHours = Math.round(lon / 15);
    if (offsetHours >= 0) {
      return `UTC+${offsetHours}`;
    } else {
      return `UTC${offsetHours}`;
    }
  } catch (error) {
    console.error('Error calculating timezone:', error);
    return 'UTC+0';
  }
};

// Comprehensive city to timezone mapping (cleaned up to remove duplicates)
const cityTimezoneMap = {
  // USA
  "New York, NY, USA": "America/New_York",
  "Los Angeles, CA, USA": "America/Los_Angeles",
  "Chicago, IL, USA": "America/Chicago",
  "Houston, TX, USA": "America/Chicago",
  "Phoenix, AZ, USA": "America/Phoenix",
  "Philadelphia, PA, USA": "America/New_York",
  "San Antonio, TX, USA": "America/Chicago",
  "San Diego, CA, USA": "America/Los_Angeles",
  "Dallas, TX, USA": "America/Chicago",
  "San Jose, CA, USA": "America/Los_Angeles",
  
  // Europe
  "London, UK": "Europe/London",
  "Paris, France": "Europe/Paris",
  "Berlin, Germany": "Europe/Berlin",
  "Madrid, Spain": "Europe/Madrid",
  "Rome, Italy": "Europe/Rome",
  "Amsterdam, Netherlands": "Europe/Amsterdam",
  "Vienna, Austria": "Europe/Vienna",
  "Prague, Czech Republic": "Europe/Prague",
  "Budapest, Hungary": "Europe/Budapest",
  "Warsaw, Poland": "Europe/Warsaw",
  "Moscow, Russia": "Europe/Moscow",
  
  // Asia
  "Tokyo, Japan": "Asia/Tokyo",
  "Seoul, South Korea": "Asia/Seoul",
  "Beijing, China": "Asia/Shanghai",
  "Shanghai, China": "Asia/Shanghai",
  "Hong Kong": "Asia/Hong_Kong",
  "Singapore": "Asia/Singapore",
  "Bangkok, Thailand": "Asia/Bangkok",
  "Jakarta, Indonesia": "Asia/Jakarta",
  "Kuala Lumpur, Malaysia": "Asia/Kuala_Lumpur",
  "Manila, Philippines": "Asia/Manila",
  "Ho Chi Minh City, Vietnam": "Asia/Ho_Chi_Minh",
  "Mumbai, India": "Asia/Kolkata",
  "Delhi, India": "Asia/Kolkata",
  "Bangalore, India": "Asia/Kolkata",
  "Chennai, India": "Asia/Kolkata",
  "Kolkata, India": "Asia/Kolkata",
  "Hyderabad, India": "Asia/Kolkata",
  "Pune, India": "Asia/Kolkata",
  "Ahmedabad, India": "Asia/Kolkata",
  
  // Australia
  "Sydney, Australia": "Australia/Sydney",
  "Melbourne, Australia": "Australia/Melbourne",
  "Brisbane, Australia": "Australia/Brisbane",
  "Perth, Australia": "Australia/Perth",
  "Adelaide, Australia": "Australia/Adelaide",
  
  // Canada
  "Toronto, Canada": "America/Toronto",
  "Montreal, Canada": "America/Montreal",
  "Vancouver, Canada": "America/Vancouver",
  "Calgary, Canada": "America/Edmonton",
  "Edmonton, Canada": "America/Edmonton",
  "Ottawa, Canada": "America/Toronto",
  
  // Mexico
  "Mexico City, Mexico": "America/Mexico_City",
  "Guadalajara, Mexico": "America/Mexico_City",
  "Monterrey, Mexico": "America/Mexico_City",
  "Puebla, Mexico": "America/Mexico_City",
  
  // Brazil
  "São Paulo, Brazil": "America/Sao_Paulo",
  "Rio de Janeiro, Brazil": "America/Sao_Paulo",
  "Brasília, Brazil": "America/Sao_Paulo",
  "Salvador, Brazil": "America/Bahia",
  "Fortaleza, Brazil": "America/Fortaleza",
  "Belo Horizonte, Brazil": "America/Sao_Paulo",
  
  // South America
  "Buenos Aires, Argentina": "America/Argentina/Buenos_Aires",
  "Córdoba, Argentina": "America/Argentina/Cordoba",
  "Rosario, Argentina": "America/Argentina/Buenos_Aires",
  "Santiago, Chile": "America/Santiago",
  "Valparaíso, Chile": "America/Santiago",
  "Lima, Peru": "America/Lima",
  "Arequipa, Peru": "America/Lima",
  "Bogotá, Colombia": "America/Bogota",
  "Medellín, Colombia": "America/Bogota",
  "Cali, Colombia": "America/Bogota",
  "Caracas, Venezuela": "America/Caracas",
  "Maracaibo, Venezuela": "America/Caracas",
  "Quito, Ecuador": "America/Guayaquil",
  "Guayaquil, Ecuador": "America/Guayaquil",
  "Montevideo, Uruguay": "America/Montevideo",
  "Asunción, Paraguay": "America/Asuncion",
  "La Paz, Bolivia": "America/La_Paz",
  "Santa Cruz, Bolivia": "America/La_Paz",
  
  // Africa
  "Cape Town, South Africa": "Africa/Johannesburg",
  "Johannesburg, South Africa": "Africa/Johannesburg",
  "Durban, South Africa": "Africa/Johannesburg",
  "Pretoria, South Africa": "Africa/Johannesburg",
  "Port Elizabeth, South Africa": "Africa/Johannesburg",
  "Cairo, Egypt": "Africa/Cairo",
  "Alexandria, Egypt": "Africa/Cairo",
  "Giza, Egypt": "Africa/Cairo",
  "Lagos, Nigeria": "Africa/Lagos",
  "Kano, Nigeria": "Africa/Lagos",
  "Ibadan, Nigeria": "Africa/Lagos",
  "Kaduna, Nigeria": "Africa/Lagos",
  "Port Harcourt, Nigeria": "Africa/Lagos",
  "Nairobi, Kenya": "Africa/Nairobi",
  "Mombasa, Kenya": "Africa/Nairobi",
  "Kisumu, Kenya": "Africa/Nairobi",
  "Addis Ababa, Ethiopia": "Africa/Addis_Ababa",
  "Dar es Salaam, Tanzania": "Africa/Dar_es_Salaam",
  "Mwanza, Tanzania": "Africa/Dar_es_Salaam",
  "Kampala, Uganda": "Africa/Kampala",
  "Khartoum, Sudan": "Africa/Khartoum",
  "Casablanca, Morocco": "Africa/Casablanca",
  "Rabat, Morocco": "Africa/Casablanca",
  "Fez, Morocco": "Africa/Casablanca",
  "Marrakech, Morocco": "Africa/Casablanca",
  "Tunis, Tunisia": "Africa/Tunis",
  "Sfax, Tunisia": "Africa/Tunis",
  "Algiers, Algeria": "Africa/Algiers",
  "Oran, Algeria": "Africa/Algiers",
  "Tehran, Iran": "Asia/Tehran",
  "Mashhad, Iran": "Asia/Tehran",
  "Isfahan, Iran": "Asia/Tehran",
  "Tabriz, Iran": "Asia/Tehran",
  "Shiraz, Iran": "Asia/Tehran",
  "Karaj, Iran": "Asia/Tehran",
  "Qom, Iran": "Asia/Tehran",
  "Kermanshah, Iran": "Asia/Tehran",
  "Urmia, Iran": "Asia/Tehran",
  "Rasht, Iran": "Asia/Tehran",
  "Yazd, Iran": "Asia/Tehran",
  "Ardabil, Iran": "Asia/Tehran",
  "Bandar Abbas, Iran": "Asia/Tehran",
  "Arak, Iran": "Asia/Tehran",
  "Eslamshahr, Iran": "Asia/Tehran",
  "Zanjan, Iran": "Asia/Tehran",
  "Sanandaj, Iran": "Asia/Tehran",
  "Qazvin, Iran": "Asia/Tehran",
  "Kerman, Iran": "Asia/Tehran",
  "Hamadan, Iran": "Asia/Tehran",
  "Yasuj, Iran": "Asia/Tehran",
  "Bojnord, Iran": "Asia/Tehran",
  "Bushehr, Iran": "Asia/Tehran",
  "Gorgan, Iran": "Asia/Tehran",
  "Ilam, Iran": "Asia/Tehran",
  "Sari, Iran": "Asia/Tehran",
  "Shahrekord, Iran": "Asia/Tehran"
};




// Test function to verify timezone calculation
const getTimezoneFromCity = (cityName, birthDate = null) => {
  // First, try exact match in our cityTimezoneMap
  let timezoneName = cityTimezoneMap[cityName];
  
  // If not found, try to find a match by extracting the city name and searching
  if (!timezoneName) {
    // Extract just the city name (before the first comma)
    const cityOnly = cityName.split(',')[0].trim();
    
    // Search for cities that start with the same name
    for (const [mappedCity, mappedTimezone] of Object.entries(cityTimezoneMap)) {
      if (mappedCity.toLowerCase().startsWith(cityOnly.toLowerCase())) {
        timezoneName = mappedTimezone;
        break;
      }
    }
  }
  
  // If still not found, try to get from city-timezones library
  if (!timezoneName) {
    try {
      const cityData = cityTimezones.findFromCityStateProvince(cityName);
      if (cityData && cityData.length > 0) {
        timezoneName = cityData[0].timezone;
      }
    } catch (error) {
      console.error('Error finding timezone from city-timezones library:', error);
    }
  }
  
  if (!timezoneName) return 'UTC+0';
  
  // If we have a birth date, calculate the actual offset for that date
  if (birthDate && birthDate.trim()) {
    try {
      const date = DateTime.fromISO(birthDate);
      
      if (date.isValid) {
        // Create a DateTime object in the specific timezone for the birth date
        const cityTime = date.setZone(timezoneName);
        const offset = cityTime.offset;
        const offsetHours = offset / 60;
        const offsetString = offsetHours >= 0 ? `+${offsetHours}` : `${offsetHours}`;
        const result = `UTC${offsetString}`;
        return result;
      }
    } catch (error) {
      console.error('Error calculating timezone offset for date:', error);
    }
  }
  
  // Fallback to static offsets (these don't account for DST)
  // Note: These are base offsets and may not reflect DST for historical dates
  const timezoneOffsets = {
    // North America
    'America/New_York': 'UTC-5',
    'America/Los_Angeles': 'UTC-8',
    'America/Chicago': 'UTC-6',
    'America/Denver': 'UTC-7',
    'America/Phoenix': 'UTC-7',
    'America/Toronto': 'UTC-5',
    'America/Montreal': 'UTC-5',
    'America/Vancouver': 'UTC-8',
    'America/Mexico_City': 'UTC-6',
    
    // South America
    'America/Sao_Paulo': 'UTC-3',
    'America/Argentina/Buenos_Aires': 'UTC-3',
    'America/Santiago': 'UTC-3',
    'America/Lima': 'UTC-5',
    'America/Bogota': 'UTC-5',
    'America/Caracas': 'UTC-4',
    'America/Guayaquil': 'UTC-5',
    'America/Montevideo': 'UTC-3',
    'America/Asuncion': 'UTC-3',
    'America/La_Paz': 'UTC-4',
    
    // Europe
    'Europe/London': 'UTC+0',
    'Europe/Paris': 'UTC+1',
    'Europe/Berlin': 'UTC+1',
    'Europe/Madrid': 'UTC+1',
    'Europe/Rome': 'UTC+1',
    'Europe/Amsterdam': 'UTC+1',
    'Europe/Vienna': 'UTC+1',
    'Europe/Prague': 'UTC+1',
    'Europe/Budapest': 'UTC+1',
    'Europe/Warsaw': 'UTC+1',
    'Europe/Moscow': 'UTC+3',
    
    // Asia
    'Asia/Tokyo': 'UTC+9',
    'Asia/Seoul': 'UTC+9',
    'Asia/Shanghai': 'UTC+8',
    'Asia/Hong_Kong': 'UTC+8',
    'Asia/Singapore': 'UTC+8',
    'Asia/Bangkok': 'UTC+7',
    'Asia/Jakarta': 'UTC+7',
    'Asia/Kuala_Lumpur': 'UTC+8',
    'Asia/Manila': 'UTC+8',
    'Asia/Ho_Chi_Minh': 'UTC+7',
    'Asia/Kolkata': 'UTC+5:30',
    'Asia/Tehran': 'UTC+3:30',
    
    // Australia
    'Australia/Sydney': 'UTC+10',
    'Australia/Melbourne': 'UTC+10',
    'Australia/Brisbane': 'UTC+10',
    'Australia/Perth': 'UTC+8',
    'Australia/Adelaide': 'UTC+9:30',
    
    // Africa
    'Africa/Cairo': 'UTC+2',
    'Africa/Lagos': 'UTC+1',
    'Africa/Nairobi': 'UTC+3',
    'Africa/Johannesburg': 'UTC+2',
    
    // Pacific
    'Pacific/Auckland': 'UTC+12'
  };
  
  return timezoneOffsets[timezoneName] || 'UTC+0';
};

// Debounce function for search
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Custom styles for react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: state.isFocused
      ? '1px solid rgba(255, 215, 0, 0.6)'
      : '1px solid rgba(255, 255, 255, 0.18)',
    borderRadius: '16px',
    minHeight: '48px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(255, 215, 0, 0.18)' : 'none',
    backdropFilter: 'blur(12px)',
    '&:hover': {
      border: '1px solid rgba(255, 215, 0, 0.45)'
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'rgba(20, 10, 15, 0.96)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '16px',
    zIndex: 1000,
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.35)'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? 'rgba(255, 215, 0, 0.12)' : 'transparent',
    color: 'white',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 215, 0, 0.18)'
    }
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }),
  input: (provided) => ({
    ...provided,
    color: 'white'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(255, 255, 255, 0.6)'
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    color: 'rgba(255, 255, 255, 0.6)'
  }),
  loadingMessage: (provided) => ({
    ...provided,
    color: 'rgba(255, 255, 255, 0.6)'
  })
};

const BirthChartFormModern = ({ onChartReady }) => {
  const [formData, setFormData] = useState({
    name: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    latitude: '',
    longitude: '',
    timeZone: '',
    houseSystem: 'whole-sign' // Add house system selection
  });
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModernPlanets, setShowModernPlanets] = useState(false);
  const { fetchCoordinates, loading: geocodingLoading, error: geocodingError } = useCityToCoordinates();
  const [chartData, setChartData] = useState(null);
  const [showHouseSystemHelp, setShowHouseSystemHelp] = useState(false);
  
  // React-select state
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  
  // Advanced settings state
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [useCoordinates, setUseCoordinates] = useState(false);
  const [coordinateError, setCoordinateError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSaveOption, setShowSaveOption] = useState(false);
  const [savedBirthData, setSavedBirthData] = useState([]);
  const [selectedSavedData, setSelectedSavedData] = useState(null);
  
  // Timezone change notification state
  const [timezoneNotification, setTimezoneNotification] = useState(null);
  const [previousTimezone, setPreviousTimezone] = useState(null);
  const { db } = useFirebase();
  const { user } = useAuth();

  // Save modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const notificationStatus = timezoneNotification
    ? (() => {
        if (timezoneNotification.type === 'save-success') {
          return { label: 'Saved Successfully', color: '#28a745' };
        }
        if (timezoneNotification.type === 'auth-required') {
          return { label: 'Log In Required', color: '#ffd700' };
        }
        if (timezoneNotification.type === 'error') {
          return { label: 'Save Failed', color: '#dc3545' };
        }
        if (timezoneNotification.isDSTChange) {
          return { label: 'DST Detected', color: '#ff6b35' };
        }
        return { label: 'Timezone Updated', color: '#ffd700' };
      })()
    : null;

  // Create options from predefined cities
  useEffect(() => {
    const predefinedOptions = uniqueCities.map(city => ({
      value: city.name,
      label: city.name,
      lat: city.lat,
      lon: city.lon,
      timezone: getTimezoneFromCity(city.name, formData.birthDate),
      source: 'predefined'
    }));
    setLocationOptions(predefinedOptions);
  }, [formData.birthDate]);



  // Function to show timezone change notification
  const showTimezoneNotification = (oldTimezone, newTimezone, location, date) => {
    if (oldTimezone && newTimezone && oldTimezone !== newTimezone && location && date) {
      // Format the date nicely
      const formattedDate = new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Determine if it's a DST change
      const isDSTChange = Math.abs(parseInt(oldTimezone.replace('UTC', '')) - parseInt(newTimezone.replace('UTC', ''))) === 1;
      
      const notification = {
        type: 'timezone-change',
        message: isDSTChange 
          ? `Daylight Saving Time detected for ${location} on ${formattedDate}: ${oldTimezone} → ${newTimezone}`
          : `Timezone updated for ${location} on ${formattedDate}: ${oldTimezone} → ${newTimezone}`,
        oldTimezone,
        newTimezone,
        location,
        date: formattedDate,
        isDSTChange
      };
      setTimezoneNotification(notification);
      
      // Auto-hide after 6 seconds for DST changes, 4 seconds for regular changes
      setTimeout(() => {
        setTimezoneNotification(null);
      }, isDSTChange ? 6000 : 4000);
    }
  };

  // Handle location search with react-select
  const handleLocationSearch = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return Promise.resolve([]);
    }

    setIsLoadingOptions(true);
    
    return new Promise((resolve) => {
      try {
        // Search through predefined cities first
        const predefinedMatches = uniqueCities
          .filter(city => city.name.toLowerCase().includes(inputValue.toLowerCase()))
          .map(city => ({
            value: city.name,
            label: city.name,
            lat: city.lat,
            lon: city.lon,
            timezone: getTimezoneFromCity(city.name, formData.birthDate),
            source: 'predefined'
          }));

        // Search for additional cities using city-timezones
        const additionalResults = cityTimezones.findFromCityStateProvince(inputValue);
        let additionalOptions = [];
        
        if (additionalResults && additionalResults.length > 0) {
          additionalOptions = additionalResults
            .filter(city => !predefinedMatches.some(pre => pre.value === city.city))
            .slice(0, 5)
            .map(city => ({
              value: `${city.city}${city.province ? `, ${city.province}` : ''}${city.country ? `, ${city.country}` : ''}`,
              label: `${city.city}${city.province ? `, ${city.province}` : ''}${city.country ? `, ${city.country}` : ''}`,
              lat: parseFloat(city.lat),
              lon: parseFloat(city.lng),
              timezone: city.timezone,
              source: 'database'
            }));
        }

        // Combine and return options
        const allOptions = [...predefinedMatches, ...additionalOptions];
        resolve(allOptions.slice(0, 10));
      } catch (error) {
        console.error('Location search error:', error);
        resolve([]);
      } finally {
        setIsLoadingOptions(false);
      }
    });
  }, []);

  // Handle location selection
  const handleLocationChange = async (selectedOption, actionMeta) => {
    if (actionMeta.action === 'clear') {
      setSelectedLocation(null);
      setFormData(prev => ({
        ...prev,
        birthPlace: '',
        latitude: '',
        longitude: '',
        timeZone: ''
      }));
      setShowCoordinates(false);
      return;
    }

    if (!selectedOption) return;

    setSelectedLocation(selectedOption);

    // If it's a predefined or database city
    if (selectedOption.lat && selectedOption.lon) {
      const oldTimezone = formData.timeZone;
      // Always recalculate timezone to ensure it's based on the current birth date
      const newTimezone = getTimezoneFromCity(selectedOption.value, formData.birthDate);
      
      setFormData(prev => ({
        ...prev,
        birthPlace: selectedOption.value,
        latitude: selectedOption.lat.toString(),
        longitude: selectedOption.lon.toString(),
        timeZone: newTimezone
      }));
      setShowCoordinates(true);
      
      // Show timezone change notification if applicable
      showTimezoneNotification(oldTimezone, newTimezone, selectedOption.value, formData.birthDate);
    } else {
      // If it's a custom location, try to geocode it
      try {
        const coords = await fetchCoordinates(selectedOption.value);
        if (coords) {
          let timezoneOffset = 'UTC+0';
          try {
            // Use birth date for timezone calculation if available
            const dateToUse = formData.birthDate ? DateTime.fromISO(formData.birthDate) : DateTime.now();
            const timezoneName = getTimezoneFromCoordinates(coords.lat, coords.lon, formData.birthDate);
            const cityTime = dateToUse.setZone(timezoneName);
            const offset = cityTime.offset;
            const offsetHours = offset / 60;
            const offsetString = offsetHours >= 0 ? `+${offsetHours}` : `${offsetHours}`;
            timezoneOffset = `UTC${offsetString}`;
          } catch (error) {
            console.error('Error calculating timezone offset:', error);
            timezoneOffset = 'UTC+0';
          }
          
          setFormData(prev => ({
            ...prev,
            birthPlace: selectedOption.value,
            latitude: coords.lat.toString(),
            longitude: coords.lon.toString(),
            timeZone: timezoneOffset
          }));
          setShowCoordinates(true);
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      }
    }
  };

  // Custom option component to show timezone info
  const CustomOption = ({ data, innerProps, isFocused }) => (
    <div
      {...innerProps}
      style={{
        padding: '8px 12px',
        backgroundColor: isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}
    >
      <MapPin size={14} style={{ opacity: 0.7, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{data.label}</div>
      </div>
    </div>
  );

  // Custom no options message
  const NoOptionsMessage = ({ inputValue }) => (
    <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
      {inputValue.length < 2 ? 'Start typing to search cities...' : 'No cities found'}
    </div>
  );

  // Custom loading message
  const LoadingMessage = () => (
    <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
      <Search size={14} style={{ marginRight: '8px', display: 'inline' }} />
      Searching...
    </div>
  );

  // Custom single value component
  const CustomSingleValue = ({ data }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <MapPin size={14} style={{ opacity: 0.7, flexShrink: 0 }} />
      <div>
        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{data.label}</div>
      </div>
    </div>
  );

  // Coordinate validation functions
  const validateLatitude = (lat) => {
    const num = parseFloat(lat);
    return !isNaN(num) && num >= -90 && num <= 90;
  };

  const validateLongitude = (lon) => {
    const num = parseFloat(lon);
    return !isNaN(num) && num >= -180 && num <= 180;
  };

  const handleCoordinateChange = (name, value) => {
    setCoordinateError('');
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate coordinates when both are present
    if (name === 'latitude' && formData.longitude) {
      if (!validateLatitude(value)) {
        setCoordinateError('Latitude must be between -90 and 90 degrees');
      } else if (!validateLongitude(formData.longitude)) {
        setCoordinateError('Longitude must be between -180 and 180 degrees');
      }
    } else if (name === 'longitude' && formData.latitude) {
      if (!validateLatitude(formData.latitude)) {
        setCoordinateError('Latitude must be between -90 and 90 degrees');
      } else if (!validateLongitude(value)) {
        setCoordinateError('Longitude must be between -180 and 180 degrees');
      }
    }
  };

  const handleUseCoordinatesToggle = () => {
    setUseCoordinates(!useCoordinates);
    if (useCoordinates) {
      // Switching back to city search, clear coordinates
      setFormData(prev => ({
        ...prev,
        latitude: '',
        longitude: '',
        timeZone: ''
      }));
      setShowCoordinates(false);
      setSelectedLocation(null);
    } else {
      // Switching to coordinates, clear city selection
      setFormData(prev => ({
        ...prev,
        birthPlace: ''
      }));
      setSelectedLocation(null);
    }
    setCoordinateError('');
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prevData) => {
      const newData = { ...prevData, [name]: value };
      
      // If birth date changed and we have a location selected, update timezone
      if (name === 'birthDate' && newData.birthPlace) {
        const selectedCity = uniqueCities.find(city => city.name === newData.birthPlace);
        if (selectedCity) {
          try {
            const dateToUse = value ? DateTime.fromISO(value) : DateTime.now();
            let timezoneName = 'UTC';
            
            // Use getTimezoneFromCity for all predefined cities to ensure proper DST handling
            const newTimezone = getTimezoneFromCity(selectedCity.name, value);
            
            // Show timezone change notification
            showTimezoneNotification(newData.timeZone, newTimezone, selectedCity.name, value);
            
            return { ...newData, timeZone: newTimezone };
          } catch (error) {
            console.error('Error updating timezone for date change:', error);
          }
        }
      }
      
      // Check if all required fields are filled to show save option
      const allFieldsFilled = newData.birthDate && 
                             newData.birthTime && 
                             newData.birthPlace;
      setShowSaveOption(allFieldsFilled);
      
      return newData;
    });
  };

  const handlePlaceChange = async (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'custom') {
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '', timeZone: '' }));
    } else if (selectedValue === '') {
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '', timeZone: '' }));
    } else {
      setShowCoordinates(false);
      const selectedCity = uniqueCities.find(city => city.name === selectedValue);
      if (selectedCity) {
        // Get timezone offset using Luxon with birth date consideration
        let timezoneOffset = 'UTC+0';
        try {
          // Use getTimezoneFromCity for all predefined cities to ensure proper DST handling
          timezoneOffset = getTimezoneFromCity(selectedCity.name, formData.birthDate);
        } catch (error) {
          console.error('Error calculating timezone offset:', error);
          timezoneOffset = 'UTC+0';
        }
        
        const oldTimezone = formData.timeZone;
        
        setFormData(prev => ({
          ...prev,
          birthPlace: selectedCity.name,
          latitude: selectedCity.lat.toString(),
          longitude: selectedCity.lon.toString(),
          timeZone: timezoneOffset
        }));
        setShowCoordinates(true);
        
        // Show timezone change notification if applicable
        showTimezoneNotification(oldTimezone, timezoneOffset, selectedCity.name, formData.birthDate);
      }
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const latitude = parseFloat(formData.latitude) || 0;
      const longitude = parseFloat(formData.longitude) || 0;
      const { birthDate, birthTime, birthPlace } = formData;
      if (!birthDate || !birthTime || !birthPlace) throw new Error('Please enter all required fields.');
      
      let timezoneName = cityTimezoneMap[birthPlace] || 'UTC';
      
      // Create local time properly
      const local = DateTime.fromISO(`${birthDate}T${birthTime}`, { zone: timezoneName });
      if (!local.isValid) throw new Error('Invalid date or time format.');
      
      // Check for timezone changes (DST, etc.)
      const utc = local.toUTC();
      const timezoneInfo = local.zone;
      const isDST = timezoneInfo.isLuxonZone ? timezoneInfo.isDST(local.ts) : false;
      
      // Show timezone change notification if applicable
      if (isDST) {
        const notification = {
          type: 'timezone-change',
          message: `Daylight Saving Time (DST) was in effect for ${birthPlace} on ${birthDate}. Your birth time has been adjusted accordingly.`,
          oldTimezone: 'Standard Time',
          newTimezone: 'Daylight Saving Time',
          location: birthPlace,
          date: birthDate,
          isDSTChange: true
        };
        setTimezoneNotification(notification);
        
        // Auto-hide after 6 seconds
        setTimeout(() => {
          setTimezoneNotification(null);
        }, 6000);
      }
      
      // FIXED: Pass local time components directly to avoid double conversion
      // Note: CircularNatalHoroscopeJS expects 0-indexed months, Luxon uses 1-indexed
      const chart = await calculateBirthChartWithLibrary(
        local.year,
        local.month - 1, // Convert to 0-indexed for CircularNatalHoroscopeJS
        local.day,
        local.hour,
        local.minute,
        local.second,
        latitude,
        longitude,
        showModernPlanets,
        formData.houseSystem
      );
      setChartData(chart);
      if (onChartReady) onChartReady(chart);
    } catch (err) {
      setError("An error occurred while generating the birth chart. Please check your data and try again. " + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChart = () => {
    if (!chartData) {
      setError('No chart data to save. Please generate a birth chart first.');
      return;
    }
    
    setShowSaveModal(true);
  };

  const confirmSave = async () => {
    try {
      // Format the reading for journal
      const journalEntry = {
        userId: window.firebase.auth().currentUser?.uid || '',
        type: 'birth-chart',
        title: `Birth Chart - ${formData.name || 'Unnamed'}`,
        content: `Birth Chart generated for ${formData.name || 'Unnamed'} on ${new Date().toLocaleDateString()}`,
        details: {
          name: formData.name,
          birthDate: formData.birthDate,
          birthTime: formData.birthTime,
          birthPlace: formData.birthPlace,
          latitude: formData.latitude,
          longitude: formData.longitude,
          timeZone: formData.timeZone,
          houseSystem: formData.houseSystem,
          chartData: chartData
        },
        timestamp: new Date().toISOString()
      };
      
      // Save to localStorage for backup
      const savedReadings = JSON.parse(localStorage.getItem('birthChartSavedReadings') || '[]');
      const newReading = {
        id: Date.now(),
        date: new Date().toISOString(),
        chartData: chartData,
        formData: formData,
        type: 'birth-chart'
      };
      
      const updatedReadings = [...savedReadings, newReading];
      localStorage.setItem('birthChartSavedReadings', JSON.stringify(updatedReadings));
      
      // Save to Firebase journal if user is logged in
      if (window.firebase && window.firebase.auth && window.firebase.auth().currentUser) {
        const user = window.firebase.auth().currentUser;
        const entriesRef = window.firebase
          .firestore()
          .collection('journal')
          .doc(user.uid)
          .collection('entries');
        await entriesRef.add(journalEntry);
      }
      
      setSaveSuccess(true);
      
      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowSaveModal(false);
        setSaveSuccess(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving chart:', error);
      alert('Error saving chart. Please try again.');
    }
  };

  // Load saved birth data from Firestore when logged in
  useEffect(() => {
    let isMounted = true;
    const loadBirthData = async () => {
      if (!user?.uid) {
        setSavedBirthData([]);
        return;
      }
      try {
        const ref = collection(db, 'users', user.uid, 'birthData');
        const snapshot = await getDocs(ref);
        if (!isMounted) return;
        const items = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSavedBirthData(items);
      } catch (error) {
        console.error('Error loading saved birth data:', error);
      }
    };
    loadBirthData();
    return () => {
      isMounted = false;
    };
  }, [db, user]);

  const handleSaveBirthData = async () => {
    const birthDataToSave = {
      id: Date.now(),
      name: formData.name || 'Unnamed Chart',
      birthDate: formData.birthDate,
      birthTime: formData.birthTime,
      birthPlace: formData.birthPlace,
      latitude: formData.latitude,
      longitude: formData.longitude,
      timeZone: formData.timeZone,
      houseSystem: formData.houseSystem,
      savedAt: new Date().toISOString()
    };

    if (!user?.uid) {
      const notification = {
        type: 'auth-required',
        message: 'Please log in to save birth data.',
        duration: 4000
      };
      setTimezoneNotification(notification);
      setTimeout(() => setTimezoneNotification(null), 4000);
      return;
    }

    try {
      // Keep a local optimistic copy so the dropdown updates immediately
      setSavedBirthData(prev => [...prev, birthDataToSave]);

      const ref = collection(db, 'users', user.uid, 'birthData');
      const docRef = await addDoc(ref, {
        name: birthDataToSave.name,
        birthDate: birthDataToSave.birthDate,
        birthTime: birthDataToSave.birthTime,
        birthPlace: birthDataToSave.birthPlace,
        latitude: birthDataToSave.latitude,
        longitude: birthDataToSave.longitude,
        timeZone: birthDataToSave.timeZone,
        houseSystem: birthDataToSave.houseSystem,
        savedAt: serverTimestamp()
      });

      // Replace the optimistic local entry with the server one so IDs stay in sync
      const syncedEntry = { ...birthDataToSave, id: docRef.id };
      setSavedBirthData(prev => [
        ...prev.filter(item => item.id !== birthDataToSave.id),
        syncedEntry
      ]);

      setSaveSuccess(true);
      
      const notification = {
        type: 'save-success',
        message: `Birth info saved successfully! You can now load it from the dropdown above.`,
        duration: 4000
      };
      setTimezoneNotification(notification);
      
      setTimeout(() => {
        setSaveSuccess(false);
        setTimezoneNotification(null);
      }, 4000);
    } catch (error) {
      console.error('Error saving birth data:', error);
      setTimezoneNotification({
        type: 'error',
        message: 'Could not save to your account (permission or network blocked). Kept a local copy—please try again when signed in and online.',
        duration: 4000
      });
      setTimeout(() => setTimezoneNotification(null), 4000);
    }
  };

  const handleLoadSavedData = (savedData) => {
    setFormData({
      name: savedData.name,
      birthDate: savedData.birthDate,
      birthTime: savedData.birthTime,
      birthPlace: savedData.birthPlace,
      latitude: savedData.latitude,
      longitude: savedData.longitude,
      timeZone: savedData.timeZone,
      houseSystem: savedData.houseSystem
    });
    setSelectedSavedData(savedData);
  };

  const houseSystems = [
    { value: 'whole-sign', label: 'Whole Sign (Ancient)', description: 'Traditional house system based on the 12 zodiac signs. Each house represents a specific zodiac sign.', era: 'Ancient', complexity: 'Low' },
    { value: 'placidus', label: 'Placidus (Modern)', description: 'Most popular modern house system, based on the ecliptic. Houses are divided into 12 equal parts.', era: 'Modern', complexity: 'Medium' },
    { value: 'koch', label: 'Koch (Modern)', description: 'Another modern house system, similar to Placidus but with slightly different house divisions.', era: 'Modern', complexity: 'Medium' },
    { value: 'equal-house', label: 'Equal House (Ancient)', description: 'Equal-division house system, where each house is an equal part of the ecliptic.', era: 'Ancient', complexity: 'Low' },
    { value: 'campanus', label: 'Campanus (Medieval)', description: 'Medieval house system, based on the 12 houses of the zodiac, but with different house divisions.', era: 'Medieval', complexity: 'Medium' },
    { value: 'regiomontanus', label: 'Regiomontanus (Medieval)', description: 'Medieval house system, based on the 12 houses of the zodiac, with a different division of the ecliptic.', era: 'Medieval', complexity: 'Medium' },
    { value: 'topocentric', label: 'Topocentric (Modern)', description: 'Modern house system that takes into account the observer\'s location and the celestial sphere.', era: 'Modern', complexity: 'High' }
  ];

  return (
    <div 
      className="birthchart-modern full-screen-image-container full-height margin-top-0"
      style={{
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'none',
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <div className="ascendant-display-container">
        <div className="ascendant-header" style={{ marginBottom: '0.5rem' }}>
          <div className="ascendant-title">
            <h1>Birth Chart Generator</h1>
          </div>
        </div>

        {/* Timezone Change Notification */}
        {timezoneNotification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: `2px solid ${notificationStatus?.color || '#ffd700'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: 1000,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                backgroundColor: notificationStatus?.color || '#ffd700', 
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></div>
              <span style={{ 
                color: notificationStatus?.color || '#ffd700', 
                fontWeight: '600' 
              }}>
                {notificationStatus?.label}
              </span>
            </div>
            <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
              {timezoneNotification.message}
            </div>
            <button
              onClick={() => setTimezoneNotification(null)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'none',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '0',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        )}

      {!chartData && (
        <form onSubmit={handleSubmit} className="ascendant-form" style={{ padding: '0.5rem', marginBottom: '0.4rem' }}>
          {/* Load Previous Birth Info - Comes first for users who already have saved data */}
          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <User size={14} />
              <span style={{ fontSize: '0.9rem' }}>Load Previous Birth Info</span>
            </div>
            <div className="form-field">
              <select
                value={selectedSavedData ? selectedSavedData.id : ''}
                onChange={(e) => {
                  const selected = savedBirthData.find(data => data.id === e.target.value || data.id === parseInt(e.target.value));
                  if (selected) {
                    handleLoadSavedData(selected);
                  }
                }}
                style={{
                  padding: '0 0.6rem',
                  fontSize: '0.85rem',
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  color: 'white'
                }}
              >
                <option value="">Select previously saved birth info...</option>
                {savedBirthData.map(data => (
                  <option key={data.id} value={data.id}>
                    {[data.name, data.birthDate, data.birthTime, data.birthPlace].filter(Boolean).join(' - ')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <User size={14} />
              <span style={{ fontSize: '0.9rem' }}>Name</span>
            </div>
            <div className="form-field">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                style={{ 
                  padding: '0 0.6rem', 
                  fontSize: '0.85rem'
                }}
              />
            </div>
          </div>

          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <Calendar size={14} />
              <span style={{ fontSize: '0.9rem' }}>Birth Details</span>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label style={{ fontSize: '0.75rem' }}>Birth Date</label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                  style={{ 
                    padding: '0 0.6rem', 
                    fontSize: '0.85rem'
                  }}
                />
              </div>
              <div className="form-field">
                <label style={{ fontSize: '0.75rem' }}>Birth Time</label>
                <input
                  type="time"
                  name="birthTime"
                  value={formData.birthTime}
                  onChange={handleChange}
                  required
                  style={{ 
                    padding: '0 0.6rem', 
                    fontSize: '0.85rem'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <MapPin size={14} />
              <span style={{ fontSize: '0.9rem' }}>Birth Location</span>
            </div>
            
            {!useCoordinates ? (
              <div className="form-field">
                <AsyncSelect
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  loadOptions={handleLocationSearch}
                  placeholder="Select your city from the list or start typing to search..."
                  isClearable
                  isSearchable
                  isLoading={isLoadingOptions}
                  loadingMessage={LoadingMessage}
                  noOptionsMessage={NoOptionsMessage}
                  components={{
                    Option: CustomOption,
                    SingleValue: CustomSingleValue
                  }}
                  styles={customSelectStyles}
                  cacheOptions={false}
                  defaultOptions={locationOptions}
                />
                {geocodingError && <div className="error-message">{geocodingError}</div>}
              </div>
            ) : (
              <div className="form-field">
                <div className="form-row">
                  <div className="form-field">
                    <label>Latitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      name="latitude"
                      value={formData.latitude}
                      onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
                      placeholder="e.g., 40.7128"
                      required
                      style={{ 
                        padding: '0 0.6rem', 
                        fontSize: '0.85rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                        color: 'white'
                      }}
                    />
                  </div>
                  <div className="form-field">
                    <label>Longitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      name="longitude"
                      value={formData.longitude}
                      onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
                      placeholder="e.g., -74.0060"
                      required
                      style={{ 
                        padding: '0 0.6rem', 
                        fontSize: '0.85rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                        color: 'white'
                      }}
                    />
                  </div>
                </div>
                {coordinateError && (
                  <div className="error-message" style={{ marginTop: '0.5rem' }}>
                    {coordinateError}
                  </div>
                )}
                <small style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  Enter coordinates manually (Latitude: -90 to 90, Longitude: -180 to 180)
                </small>
              </div>
            )}
            
            {showCoordinates && (
              <div className="form-field">
                <label>Time Zone</label>
                <input
                  type="text"
                  name="timeZone"
                  value={formData.timeZone}
                  onChange={handleChange}
                  placeholder="e.g., UTC+1"
                  required
                  readOnly
                  style={{ 
                    padding: '0 0.6rem', 
                    fontSize: '0.85rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    color: 'white'
                  }}
                />
                <small>Automatically calculated from location and date</small>
              </div>
            )}
          </div>

          {/* Advanced Settings Section */}
          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <Info size={14} />
              <span style={{ fontSize: '0.9rem' }}>Advanced Settings</span>
            </div>
            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox" 
                  checked={showAdvancedSettings} 
                  onChange={(e) => setShowAdvancedSettings(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.85rem', color: 'white', cursor: 'pointer' }}>
                  Show advanced options
                </span>
              </label>
            </div>
            
            {showAdvancedSettings && (
              <>
                {/* Coordinate Search Option */}
                <div className="form-field" style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox" 
                      checked={useCoordinates} 
                      onChange={handleUseCoordinatesToggle}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: 'white', cursor: 'pointer' }}>
                      Search by coordinates instead of city
                    </span>
                  </label>
                  <small style={{ marginTop: '0.25rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Enter latitude and longitude manually for precise location
                  </small>
                </div>

                {/* House System Selection */}
                <div className="form-field" style={{ marginTop: '1rem' }}>
                  <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <i className="fas fa-home" style={{ fontSize: '14px' }}></i>
                    <span style={{ fontSize: '0.9rem' }}>House System</span>
                    <button
                      type="button"
                      onClick={() => setShowHouseSystemHelp(!showHouseSystemHelp)}
                      style={{
                        background: 'none',
                        color: 'rgba(255, 255, 255, 0.7)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                      title="Learn about house systems"
                    >
                      <i className="fas fa-question-circle"></i>
                    </button>
                  </div>
                  <select
                    name="houseSystem"
                    value={formData.houseSystem}
                    onChange={handleChange}
                    style={{ 
                      padding: '0 0.6rem', 
                      fontSize: '0.85rem',
                      width: '100%',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '4px',
                      color: 'white'
                    }}
                  >
                    {houseSystems.map(system => (
                      <option key={system.value} value={system.value} style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                        {system.label}
                      </option>
                    ))}
                  </select>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.25rem' }}>
                    Choose your preferred house system. Whole Sign is traditional, Placidus is most popular in modern astrology.
                  </p>
                </div>

                {/* Chart Options */}
                <div className="form-field" style={{ marginTop: '1rem' }}>
                  <div className="section-title" style={{ marginBottom: '0.5rem' }}>
                    <Info size={14} />
                    <span style={{ fontSize: '0.9rem' }}>Chart Options</span>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox" 
                      checked={showModernPlanets} 
                      onChange={(e) => setShowModernPlanets(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: 'white', cursor: 'pointer' }}>
                      Include Modern Planets (Uranus, Neptune)
                    </span>
                  </label>
                </div>
              </>
            )}
            
            {/* House System Help Modal */}
            {showHouseSystemHelp && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                zIndex: 1000,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1rem'
              }}>
                <div style={{
                  backgroundColor: '#2c3e50',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  maxWidth: '600px',
                  maxHeight: '80vh',
                  overflow: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ color: 'white', margin: 0 }}>House Systems Guide</h3>
                    <button
                      onClick={() => setShowHouseSystemHelp(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        fontSize: '1.5rem',
                        cursor: 'pointer'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    {houseSystems.map(system => (
                      <div key={system.value} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}>
                        <h4 style={{ color: 'white', margin: '0 0 0.5rem 0' }}>{system.label}</h4>
                        <p style={{ margin: '0.5rem 0' }}>{system.description}</p>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                          <span style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Era: {system.era}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Complexity: {system.complexity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          



          <div className="form-actions" style={{ marginTop: 'auto', paddingTop: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', flex: '1', minHeight: '0' }}>
            <button
              type="button"
              className="birthchart-save-button"
              onClick={handleSaveBirthData}
              disabled={!showSaveOption}
            >
              <Save size={16} style={{ marginRight: '0.5rem' }} />
              {showSaveOption ? 'Save Birth Info' : 'Fill All Fields to Save'}
            </button>
            
            <button
              type="submit"
              className="calculate-button"
              disabled={loading}
              style={{ padding: '0.5rem 1rem', fontSize: '0.75rem' }}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="spinning" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  Generate Birth Chart
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="error-message">
          <Info size={16} />
          {error}
        </div>
      )}

      {/* Chart Results */}
      {chartData && (
        <motion.div
          key="result"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="chart-results" 
          style={{ marginTop: '2rem' }}
        >
          <div className="notification is-info is-light" style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            color: 'white'
          }}>
            <div className="content">
              <h4 className="title is-4" style={{ color: 'white' }}>Birth Chart Generated Successfully!</h4>
              <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}><strong>Date:</strong> {new Date(chartData.birthDate).toLocaleDateString()}</p>
              <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}><strong>Time:</strong> {formData.birthTime}</p>
              <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}><strong>Location:</strong> {formData.birthPlace}</p>
              <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}><strong>House System:</strong> {houseSystems.find(sys => sys.value === formData.houseSystem)?.label || formData.houseSystem}</p>
              <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}><strong>Modern Planets:</strong> {chartData.showModernPlanets ? 'Included' : 'Excluded'}</p>
            </div>
          </div>

          {/* Back to Form Button */}
          <motion.button
            className="button is-warning"
            style={{
              position: "fixed",
              top: 100,
              left: 24,
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
              color: "#000",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onClick={() => setChartData(null)}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
          >
            <ArrowLeft size={20} />
            Back to Form
          </motion.button>
        </motion.div>
      )}

      {/* Save Chart Button - Fixed Position (Same as Celtic Cross) */}
      {chartData && (
        <button 
          className="save-chart-button" 
          onClick={handleSaveChart}
          style={{
            position: "fixed",
            top: 262,
            right: 24,
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            borderRadius: "12px",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            fontWeight: "600",
            border: "none",
            background: "linear-gradient(135deg, #E53E3E 0%, #C53030 100%)",
            color: "#fff",
            cursor: "pointer",
            transition: "all 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 16px rgba(229, 62, 62, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
          }}
        >
          <Save size={16} />
          Save Reading
        </button>
      )}

      {/* Save to Journal Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowSaveModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: '#FFD700',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ✕
            </button>

            {/* Modal Content */}
            <div style={{ textAlign: 'center' }}>
              {!saveSuccess ? (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    📖
                  </div>
                  
                  <h2 style={{
                    color: '#FFD700',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '16px',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Save to Journal
                  </h2>
                  
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    Save your birth chart reading to your journal?
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    ✅
                  </div>
                  
                  <h2 style={{
                    color: '#28a745',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '16px'
                  }}>
                    Chart Saved!
                  </h2>
                  
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    lineHeight: '1.6'
                  }}>
                    Your birth chart has been successfully saved to your journal.
                  </p>
                </>
              )}

              {!saveSuccess && (
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => setShowSaveModal(false)}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      background: 'transparent',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = 'rgba(255, 215, 0, 0.5)';
                      e.target.style.color = '#FFD700';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    }}
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={confirmSave}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      color: '#1a1a2e',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(255, 215, 0, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.3)';
                    }}
                  >
                    💾 Save to Journal
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BirthChartFormModern;

